import './Coverages.css';

import React from 'react';

import {
  GetQuoteOk,
  ResetCoverageRecommendationInput,
  UpdateCoverageInput
} from '../../../../generated/rating-quoting-graphql';
import { IneligibleAmtrustQuote } from './amtrustCoverage';
import { lobTypeToProductLabel } from './configs/productLabels';
import { CoverageDetail } from './Coverage';

interface CoveragesProps {
  coverageInput: GetQuoteOk;
  onUpdateCoverage: (coverage: UpdateCoverageInput) => void;
  onResetRecommendation: (input: ResetCoverageRecommendationInput) => void;
  applicationId: string;
  isEndorsement: boolean;
  lobType: string;
  /** Deselect this LOB via its primary coverage checkbox (NCP-4545). */
  onDeselectLob?: (lobType: string) => void;
  /** Whether a LOB may be removed — false when only one LOB is active. */
  canDeselectLob?: boolean;
}

const Coverages: React.FC<CoveragesProps> = (props) => {
  const {
    coverageInput,
    onUpdateCoverage,
    applicationId,
    onResetRecommendation,
    isEndorsement,
    lobType,
    onDeselectLob,
    canDeselectLob
  } = props;
  const { coveragesData, carrierPartner, region, quoteId } = coverageInput;

  // The LOB is "selected" when its defining coverage is chosen: CGL for GL, EO
  // for EO (the same pairing Coverage.tsx uses for the deselect checkbox). An
  // available-but-unselected LOB is offered rather than described as
  // recommended, so the header becomes a question.
  const lobDefiningCoverageName = lobType === 'EO' ? 'eo' : 'cgl';
  const isLobSelected =
    coveragesData.find(
      (coverageData) => coverageData.name === lobDefiningCoverageName
    )?.chooseCoverage ?? true;
  const productLabel = lobTypeToProductLabel(lobType);

  return (
    <div className="coverages-container mt-2 md:mt-0">
      <div className="coverages-header flex justify-between bg-secondary-light px-8 py-4 font-bold text-2xl text-quartary">
        <p>
          {isLobSelected
            ? `Recommended ${productLabel} Policy:`
            : `Want to add a ${productLabel} policy?`}
        </p>
        <button
          className="text-base text-secondary underline"
          onClick={() => {
            onResetRecommendation({ quoteId, applicationId, region });
          }}
          disabled={isEndorsement}
        >
          Reset
        </button>
      </div>
      <div className="coverages-inner-container px-8 pt-4 pb-8 relative md:mb-28 lg:mb-0">
        <div className="coverages-details overflow-hidden lg:overflow-auto max-h-full">
          {coveragesData
            .filter((coverageData) => coverageData.name !== 'additionalInsured') // additonal insured is in another component
            .map((coverageData) => {
              // Since we dont enable WC on quote page, we just show it as ineligible
              if (coverageData.name === 'workersCompensation') {
                return <IneligibleAmtrustQuote key={coverageData.name} />;
              }
              return (
                <CoverageDetail
                  coverageData={coverageData}
                  key={coverageData.name}
                  onUpdateCoverage={onUpdateCoverage}
                  region={region}
                  quoteId={quoteId}
                  applicationId={applicationId}
                  carrierPartner={carrierPartner}
                  lobType={lobType}
                  onDeselectLob={onDeselectLob}
                  canDeselectLob={canDeselectLob}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default Coverages;
